import { Col, Row, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useRef, useState } from "react";
import AmazonSuspendedApplicantReportsService from "../../../../services/AmazonSuspendedApplicantReportsService";
import dayjs from "dayjs";
import { MemberCallDetail } from "../../../../types/AmazonSuspendedApplicantReports";
import ReusableChart from "../../CommonReport/SummaryReport/ReusableChart";

const NumberOfAmazonCallsReport = ({
  dateRange,
  periodCount,
}: {
  dateRange: any;
  periodCount: number;
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [dateLabels, setDateLabels] = useState<any[]>([]);
  const [chartSeries, setChartSeries] = useState<any[]>([]);
  const [chartHeight, setChartHeight] = useState<number>(200);
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1200 && tableRef.current) {
        const tableColHeight = tableRef.current.getBoundingClientRect().height;
        setChartHeight(Math.max(200, tableColHeight - 30));
      } else {
        setChartHeight(250);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line
  }, [dataSource]);

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      const periods = [];
      let start = dayjs(dateRange[0]);
      let end = dayjs(dateRange[1]);

      for (let i = 0; i < periodCount; i++) {
        const formattedStart = start.format("YYYY-MM-DDT00:00:00");
        const formattedEnd = end.format("YYYY-MM-DDT23:59:59");

        periods.push({
          start: formattedStart,
          end: formattedEnd,
          label: `${start.format("DD.MMM.YY")} - ${end.format("DD.MMM.YY")}`,
        });

        const diffDays = end.diff(start, "days") + 1;
        end = start.subtract(1, "days");
        start = end.subtract(diffDays - 1, "days");
      }

      const reversedPeriods = periods.reverse(); // Period'ları tersine çevir
      getReports(reversedPeriods);
      setDateLabels(reversedPeriods);
    }
    // eslint-disable-next-line
  }, [dateRange, periodCount]);

  const getReports = async (periods: any[]) => {
    setLoading(true);
    try {
      const promises = periods.map((period) =>
        AmazonSuspendedApplicantReportsService.getAmazonCalls(
          period.start,
          period.end
        )
      );

      const responses = await Promise.all(promises);

      const membersMap: Record<string, any> = {};

      // Veriyi dönüştürme
      responses.forEach((response, index) => {
        const periodLabel = periods[index].label;
        response.data.memberCallsDetails.forEach((member: any) => {
          if (!membersMap[member.memberName]) {
            membersMap[member.memberName] = { memberName: member.memberName };
          }
          membersMap[member.memberName][periodLabel] =
            member.numberOfAmazonCalls || 0;
        });
      });

      // `dataSource` için değerleri ayarla
      setDataSource(Object.values(membersMap));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dataSource.length > 0) {
      // Her bir member için ayrı seriler oluştur
      const memberSeries = dataSource.map((member: any) => {
        const data = dateLabels.map((period: any) => member[period.label] || 0);
        return {
          name: member.memberName, // Seri ismi member'ın adı olacak
          data,
        };
      });
  
      setChartSeries(memberSeries);
    }
  }, [dataSource, dateLabels]);  

  const columns: ColumnsType<MemberCallDetail> = [
    {
      title: "Member",
      dataIndex: "memberName",
      key: "memberName",
      fixed: "left",
      width: 200,
      ellipsis: true,
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.memberName.localeCompare(b.memberName),
      render: (text: string, record: MemberCallDetail) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={record.memberName}
        >
          <b>{record.memberName}</b>
        </div>
      ),
    },
    ...dateLabels.map((period) => ({
      title: period.label,
      dataIndex: period.label,
      key: period.label,
      align: "center" as "center",
      width: 180,
      render: (value: number) => <b>{value || 0}</b>,
    })),
  ];

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 40, marginTop: 40 }}>
      <Col span={24}>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={14} xxl={14}>
            <ReusableChart
              type="bar"
              series={chartSeries}
              categories={dateLabels.map((label) => label.label)}
              height={chartHeight}
            />
          </Col>
          <Col xs={24} xl={10} xxl={10} ref={tableRef}>
            <Table
              rowKey={(record) => record?.memberName}
              loading={loading}
              dataSource={dataSource}
              columns={columns}
              scroll={{ x: "100%" }}
              pagination={false}
              tableLayout="fixed"
              summary={(pageData) => {
                if (!pageData.length) return null;

                // Tüm kolonlar için toplamları hesapla
                const periodTotals: Record<string, number> = {};
                dateLabels.forEach((period) => {
                  periodTotals[period.label] = pageData.reduce(
                    (sum, record) => sum + (record[period.label] || 0),
                    0
                  );
                });

                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row className="summary-cell">
                      {/* İlk Kolon: Toplam Başlığı */}
                      <Table.Summary.Cell
                        index={0}
                        align="left"
                        className="summary-cell"
                      >
                        <b>Total</b>
                      </Table.Summary.Cell>

                      {/* Her period için toplamları göster */}
                      {dateLabels.map((period, index) => (
                        <Table.Summary.Cell
                          key={period.label}
                          index={index + 1}
                          align="center"
                          className="summary-cell"
                        >
                          <b>{periodTotals[period.label]}</b>
                        </Table.Summary.Cell>
                      ))}
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default NumberOfAmazonCallsReport;
